import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleLeasingGridBlock from './VehicleLeasingGridBlock.vue'
import vehicleLeasingMixin from '../../mixins/vehicleLeasingMixin'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'
import { getBooleanFromValue } from '@/global/utils/utils'

export default {
  name: 'Leasing',

  data () {
    return {
      showLeasingTable: true,
      showLeasingDetails: false,
      leasingDetailsTitle: this.$t('fleet/vehicle_leasing.leasings'),
      leasingDetailsBaseTitle: this.$t('fleet/vehicle_leasing.leasings'),
      leasingEditState: false,
      leasingCreateModeState: false,
      leasingValidationErrors: {},
      leasingPermissions: {}
    }
  },

  mixins: [
    vehicleLeasingMixin
  ],

  computed: {
    leasingConfig () {
      return {
        title: this.$t('fleet/vehicle_leasing.leasings'),
        key: 'leasing',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleLeasing),
        validationErrors: this.leasingValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.leasingEditState = !!edit
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.leasingCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showLeasingTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-leasing',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowLeasingDetails(event),
            mobileConfig: {
              title: this.$t('fleet/vehicle_leasing.leasings')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle_leasing.leasings')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewLeasing(),
              showNewBtn: getBooleanFromValue(this.leasingPermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleLeasingGridBlock,
                showDetails: () => {
                  this.showLeasingTable = false
                  this.showLeasingDetails = true
                },
                setDetailsTitle: (val) => {
                  this.leasingDetailsTitle = val
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.leasingPermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                type: {
                  custom_display_value: (item) => {
                    return item?.type?.description ? this.$t(item.type.description) : ''
                  }
                },
                currency: {
                  custom_display_value: (item) => {
                    return item?.currency?.name || ''
                  }
                },
                start_date: {
                  custom_display_value: (item) => {
                    return item?.start_date ? dateService.formatFromUtc(item.start_date, FORMATS.date) : ''
                  }
                },
                vehicle_pickup_date: {
                  custom_display_value: (item) => {
                    return item?.vehicle_pickup_date ? dateService.formatFromUtc(item.vehicle_pickup_date, FORMATS.date) : ''
                  }
                },
                expiry_date: {
                  custom_display_value: (item) => {
                    return item?.expiry_date ? dateService.formatFromUtc(item.expiry_date, FORMATS.date) : ''
                  }
                },
                alarm_on: {
                  style: {
                    justifyItems: 'center'
                  }
                },
                leasing_value_without_tax: {
                  custom_display_value: (item) => {
                    return item?.leasing_value_without_tax ? item.leasing_value_without_tax + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                leasing_value_with_tax: {
                  custom_display_value: (item) => {
                    return item?.leasing_value_with_tax ? item.leasing_value_with_tax + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                leasing_tax_amount: {
                  custom_display_value: (item) => {
                    return item?.leasing_tax_amount ? item.leasing_tax_amount + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                vehicle_net_value: {
                  custom_display_value: (item) => {
                    return item?.vehicle_net_value ? item.vehicle_net_value + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                vehicle_redemption_value: {
                  custom_display_value: (item) => {
                    return item?.vehicle_redemption_value ? item.vehicle_redemption_value + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                monthly_instalment: {
                  custom_display_value: (item) => {
                    return item?.monthly_instalment ? item.monthly_instalment + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                interest_rate: {
                  custom_display_value: (item) => {
                    return item?.interest_rate ? item.interest_rate + ' %' : ''
                  }
                },
                participation_amount: {
                  custom_display_value: (item) => {
                    return item?.participation_amount ? item.participation_amount + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                exceeded_km_price: {
                  custom_display_value: (item) => {
                    return item?.exceeded_km_price ? item.exceeded_km_price + ' ' + (item?.currency?.abbreviation || '') : ''
                  }
                },
                images: {
                  wrapperStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                  }
                },
                pdf: {
                  wrapperStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle_leasing.leasing_type'),
                  key: 'type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.contract_number'),
                  key: 'contract_number',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.foreign_id'),
                  key: 'foreign_id',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.leasing_provider'),
                  key: 'leasing_provider',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.leasing_value_without_tax'),
                  key: 'leasing_value_without_tax',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.leasing_value_with_tax'),
                  key: 'leasing_value_with_tax',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.leasing_tax_amount'),
                  key: 'leasing_tax_amount',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.vehicle_net_value'),
                  key: 'vehicle_net_value',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.vehicle_redemption_value'),
                  key: 'vehicle_redemption_value',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.monthly_instalment'),
                  key: 'monthly_instalment',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.duration'),
                  key: 'duration',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.interest_rate'),
                  key: 'interest_rate',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.participation_amount'),
                  key: 'participation_amount',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.warranty_type'),
                  key: 'warranty_type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.exceeded_km_price'),
                  key: 'exceeded_km_price',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.other_details'),
                  key: 'other_details',
                  always_visible: true,
                  component: truncatedTextDisplay
                },
                {
                  label: this.$t('fleet/vehicle_leasing.start_date'),
                  key: 'start_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.vehicle_pickup_date'),
                  key: 'vehicle_pickup_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.expiry_date'),
                  key: 'expiry_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.payment_method'),
                  key: 'payment_method',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.currency'),
                  key: 'currency',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_leasing.images'),
                  key: 'images',
                  always_visible: true,
                  component: this.tableImageIconWithCounterComponent,
                  disableDefaultClick: true,
                  onClick: (event) => {
                    this.showFilesPreviewDialog({
                      ...event,
                      fieldKey: 'images',
                      removeApiConfig: {
                        module: 'fleet',
                        route: 'vehicle-leasing/remove-image/'
                      }
                    }, 'image', 'leasing')
                  }
                },
                {
                  label: this.$t('fleet/vehicle_leasing.pdf'),
                  key: 'pdf',
                  always_visible: true,
                  component: this.tableImageIconWithCounterComponent,
                  src: '/img/icons/table-pdf-icon.svg',
                  disableDefaultClick: true,
                  onClick: (event) => {
                    this.showFilesPreviewDialog({
                      ...event,
                      fieldKey: 'pdf',
                      removeApiConfig: {
                        module: 'fleet',
                        route: 'vehicle-leasing/remove-pdf/'
                      }
                    }, 'pdf', 'leasing')
                  }
                },
                {
                  label: this.$t('fleet/vehicle_leasing.status'),
                  key: 'status',
                  always_visible: true,
                  component: this.activeIconComponent
                },
                {
                  label: this.$t('fleet/vehicle_leasing.alarm_on'),
                  key: 'alarm_on',
                  always_visible: true,
                  component: this.cellIconComponent,
                  style: {
                    textAlign: 'center'
                  },
                  src: (event) => {
                    const { data } = event || {}
                    return data === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
                  }
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowLeasingDetails(event), { visible: getBooleanFromValue(this.leasingPermissions?.canRead) }),
                activationAction(this.$t, (event) => this.onLeasingActivation(event), (event) => !!event?.data?.status),
                deleteAction(this.$t, (event) => this.onLeasingDelete(event), { visible: getBooleanFromValue(this.leasingPermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_leasing.leasing_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-leasing/types',
                      custom_display_value: (item) => {
                        return this.$t(item.description)
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'contract_number',
                    label: this.$t('fleet/vehicle_leasing.contract_number'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'foreign_id',
                    label: this.$t('fleet/vehicle_leasing.foreign_id'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_provider',
                    label: this.$t('fleet/vehicle_leasing.leasing_provider'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_value_without_tax',
                    label: this.$t('fleet/vehicle_leasing.leasing_value_without_tax'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_value_with_tax',
                    label: this.$t('fleet/vehicle_leasing.leasing_value_with_tax'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_tax_amount',
                    label: this.$t('fleet/vehicle_leasing.leasing_tax_amount'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_net_value',
                    label: this.$t('fleet/vehicle_leasing.vehicle_net_value'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_redemption_value',
                    label: this.$t('fleet/vehicle_leasing.vehicle_redemption_value'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'monthly_instalment',
                    label: this.$t('fleet/vehicle_leasing.monthly_instalment'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'duration',
                    label: this.$t('fleet/vehicle_leasing.duration'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'interest_rate',
                    label: this.$t('fleet/vehicle_leasing.interest_rate'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'participation_amount',
                    label: this.$t('fleet/vehicle_leasing.participation_amount'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'warranty_type',
                    label: this.$t('fleet/vehicle_leasing.warranty_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'exceeded_km_price',
                    label: this.$t('fleet/vehicle_leasing.exceeded_km_price'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'other_details',
                    label: this.$t('fleet/vehicle_leasing.other_details'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'start_date',
                    label: this.$t('fleet/vehicle_leasing.start_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_pickup_date',
                    label: this.$t('fleet/vehicle_leasing.vehicle_pickup_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'expiry_date',
                    label: this.$t('fleet/vehicle_leasing.expiry_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'payment_method',
                    label: this.$t('fleet/vehicle_leasing.payment_method'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'currency',
                    label: this.$t('fleet/vehicle_leasing.currency'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-leasing/currency-types'
                    },
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showLeasingDetails,
            title: this.leasingDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.leasingPermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromLeasingDetails()
            },
            onCancelTabSave: () => this.exitFromLeasingDetails(true),
            onSaveClick: () => this.onSaveLeasing()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showLeasingDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.leasingPermissions?.canUpdate),
                columnsCount: 4,
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_leasing.leasing_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-leasing/types',
                      custom_display_value: (item) => {
                        return item?.description ? this.$t(item.description) : ''
                      }
                    },
                    visible: true,
                    editable: this.leasingCreateModeState
                  },
                  {
                    key: 'contract_number',
                    label: this.$t('fleet/vehicle_leasing.contract_number'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'foreign_id',
                    label: this.$t('fleet/vehicle_leasing.foreign_id'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_provider',
                    label: this.$t('fleet/vehicle_leasing.leasing_provider'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_value_without_tax',
                    label: this.$t('fleet/vehicle_leasing.leasing_value_without_tax'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_value_with_tax',
                    label: this.$t('fleet/vehicle_leasing.leasing_value_with_tax'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'leasing_tax_amount',
                    label: this.$t('fleet/vehicle_leasing.leasing_tax_amount'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_net_value',
                    label: this.$t('fleet/vehicle_leasing.vehicle_net_value'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_redemption_value',
                    label: this.$t('fleet/vehicle_leasing.vehicle_redemption_value'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'monthly_instalment',
                    label: this.$t('fleet/vehicle_leasing.monthly_instalment'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'duration',
                    label: this.$t('fleet/vehicle_leasing.duration'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'interest_rate',
                    label: this.$t('fleet/vehicle_leasing.interest_rate'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'participation_amount',
                    label: this.$t('fleet/vehicle_leasing.participation_amount'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'images',
                    label: this.$t('fleet/vehicle/documents_tab.uploaded_images_label'),
                    type: 'files_preview',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-leasing/remove-image/'
                        }
                      }, 'image', 'leasing')
                    },
                    visible: !this.leasingEditState && !this.leasingCreateModeState,
                    editable: true
                  },
                  {
                    key: 'warranty_type',
                    label: this.$t('fleet/vehicle_leasing.warranty_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'exceeded_km_price',
                    label: this.$t('fleet/vehicle_leasing.exceeded_km_price'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'other_details',
                    label: this.$t('fleet/vehicle_leasing.other_details'),
                    type: 'textarea',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'start_date',
                    label: this.$t('fleet/vehicle_leasing.start_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          start_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'vehicle_pickup_date',
                    label: this.$t('fleet/vehicle_leasing.vehicle_pickup_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          vehicle_pickup_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'expiry_date',
                    label: this.$t('fleet/vehicle_leasing.expiry_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          expiry_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'payment_method',
                    label: this.$t('fleet/vehicle_leasing.payment_method'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'currency',
                    label: this.$t('fleet/vehicle_leasing.currency'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-leasing/currency-types'
                    },
                    visible: true,
                    editable: this.leasingCreateModeState
                  },
                  {
                    key: 'images_upload',
                    label: this.$t('fleet/vehicle_leasing.images'),
                    type: 'files_upload',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    accept: '/image*',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images_upload'
                      }, 'image', 'leasing')
                    },
                    visible: this.leasingEditState || this.leasingCreateModeState,
                    editable: true
                  },
                  {
                    key: 'pdf_upload',
                    label: this.$t('fleet/vehicle_leasing.pdf'),
                    type: 'files_upload',
                    subtype: 'pdf',
                    accept: '.pdf',
                    src: '/img/icons/table-pdf-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'pdf_upload'
                      }, 'pdf', 'leasing')
                    },
                    visible: this.leasingEditState || this.leasingCreateModeState,
                    editable: true
                  },
                  {
                    key: 'alarm_on',
                    label: this.$t('fleet/vehicle_leasing.alarm_on'),
                    type: 'base-switch',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'pdf',
                    label: this.$t('fleet/vehicle.uploaded_pdfs'),
                    type: 'files_preview',
                    subtype: 'pdf',
                    src: '/img/icons/table-pdf-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'pdf',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-leasing/remove-pdf/'
                        }
                      }, 'pdf', 'leasing')
                    },
                    visible: !this.leasingEditState && !this.leasingCreateModeState,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewLeasing () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.leasing || {}
      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }
      this.leasingDetailsTitle = this.$t('fleet/vehicle.new_vehicle_leasing')
      this.showLeasingTable = false
      this.showLeasingDetails = true
    },

    exitFromLeasingDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.leasing || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showLeasingTable = true
        this.showLeasingDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showLeasingTable = true
          this.showLeasingDetails = false
        }

        cancelSave()
      }
      this.leasingValidationErrors = {}
    },

    async onSaveLeasing () {
      try {
        const { getFormData } = registeredTabsHandlers?.leasing || {}
        const { fetchData } = registeredTableHandlers?.leasing || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const formData = makeFormData(data,
          ['images_upload', 'pdf_upload'],
          {
            vehicle_id: vehicleId
          },
          {
            start_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            },
            vehicle_pickup_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            },
            expiry_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            }
          })

        let apiUrl = 'vehicle-leasing'
        const { id: leasingId } = data || {}
        if (!this.leasingCreateModeState && leasingId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${leasingId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromLeasingDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.leasingValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    }
  }
}
